import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { fetchMe, linkGoogle as linkGoogleApi, unlinkGoogle as unlinkGoogleApi, loginUser, loginWithGoogle, registerUser, type AuthUser } from '@/api/auth'
import { queryClient } from '@/lib/queryClient'
import { clearTokens, saveTokens } from '@/lib/tokenStorage'

type AuthContextType = {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, password2: string) => Promise<void>
  googleLogin: (credential: string) => Promise<void>
  linkGoogle: (credential: string) => Promise<void>
  unlinkGoogle: () => Promise<void>
  updateTokens: (access: string, refresh: string) => void
  refreshUser: () => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  // トークンが無ければ初回 fetch 不要なので最初から非ローディング（effect 内同期 setState を回避）
  const [isLoading, setIsLoading] = useState(() => Boolean(localStorage.getItem('access_token')))

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) return
    fetchMe()
      .then(({ data }) => setUser(data))
      .catch((err) => {
        // 401 のみトークンを消去。ネットワークエラー等では消去しない
        if (err?.response?.status === 401) clearTokens()
      })
      .finally(() => setIsLoading(false))
  }, [])

  const login = async (email: string, password: string) => {
    const { data } = await loginUser(email, password)
    saveTokens(data.access, data.refresh)
    setUser(data.user)
  }

  const register = async (email: string, password: string, password2: string) => {
    const { data } = await registerUser(email, password, password2)
    saveTokens(data.access, data.refresh)
    setUser(data.user)
  }

  const googleLogin = async (credential: string) => {
    const { data } = await loginWithGoogle(credential)
    saveTokens(data.access, data.refresh)
    setUser(data.user)
  }

  const refreshUser = async () => {
    const { data } = await fetchMe()
    setUser(data)
  }

  const linkGoogle = async (credential: string) => {
    await linkGoogleApi(credential)
    await refreshUser()
  }

  const unlinkGoogle = async () => {
    await unlinkGoogleApi()
    await refreshUser()
  }

  const updateTokens = (access: string, refresh: string) => {
    saveTokens(access, refresh)
  }

  const logout = () => {
    clearTokens()
    setUser(null)
    queryClient.clear()
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, register, googleLogin, linkGoogle, unlinkGoogle, updateTokens, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

// AuthProvider と同居させるフック。Fast Refresh 警告の対象だが構成上の例外。
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
